import { useHits, useInstantSearch } from 'react-instantsearch';
import {
  HitAllResult,
  HIT_PER_PAGE,
  INDEX_NEWS,
  INDEX_PAGES,
  INDEX_VIDEOS,
  INDEX_PHOTOS,
  INDEX_ASSETS,
} from '../types';
import TitleH2 from '@/components/common/TitleH2';
import Card from '@/components/card/Card';
import CardPage from '@/components/card/CardPage';
import { CardInfo } from '@/components/types';
import CustomSortBy from '../custom/CustomSortBy';
import CustomMenu from '../custom/CustomMenu';
import TableAssetItem from '@/components/table/TableAssetItem';
import { AppConfig } from '@/utils/AppConfig';

interface Props {
  hitMapper: (hit: HitAllResult) => CardInfo | any;
  searchType: 'news' | 'all';
}

export default function CustomHits({ hitMapper, searchType }: Props) {
  const { hits, results } = useHits<HitAllResult>({});
  const { indexUiState } = useInstantSearch();

  if (!hits || hits.length === 0) return null;
  let modifiedHits = hits;

  if (searchType === 'news' && results?.page === 0) {
    const featuredHits = hits
      .filter((hit) => hit.type === 'ffbbsite_news' && hit.featured === true)
      .slice(0, 1);
    const regularHits = hits.filter((hit) => !featuredHits.map((hit) => hit.id).includes(hit.id));
    modifiedHits = [...featuredHits, ...regularHits];

    /**
     * When there is a featured news, add a static card to the last position to fill the gap
     * https://app.clickup.com/t/86bzfvemj
     */
    if (Array.isArray(featuredHits) && featuredHits.length > 0) {
      modifiedHits.push({
        title: 'Restez informés avec la newsletter FFBB !',
        date_published: '',
        category: 'newsletter',
        permalink: '/newsletter',
        type: 'ffbbsite_news',
        thumbnail: '',
        __position: 0,
        objectID: '',
        id: '',
        content:
          'Que vous soyez professionnel ou amateur, joueur, arbitre ou adhérent, journaliste ou simple curieux, restez connecté aux actualités FFBB.',
      });
    }
  }
  return (
    <div>
      {results?.hitsPerPage === HIT_PER_PAGE && hits[0]?.type && searchType == 'all' && (
        <div className="w-full">
          <TitleH2
            customFont={true}
            color="black"
            titleTracking="tracking-wide"
            size="medium"
            styles="mb-[15px] mt-[15px]"
          >
            {hits[0].type
              .replace(INDEX_NEWS, 'Articles')
              .replace(INDEX_PAGES, 'Pages')
              .replace(INDEX_VIDEOS, 'Vidéos')
              .replace(INDEX_PHOTOS, 'Photos')
              .replace(INDEX_ASSETS, 'Documents')}
          </TitleH2>
          <div className="mb-[20px] mt-[15px] flex h-[10px] w-full card-gradient md:hidden"></div>
        </div>
      )}
      {results?.hitsPerPage !== HIT_PER_PAGE &&
        hits[0]?.type !== 'directus_files' &&
        searchType === 'all' && (
          <div className="flex w-full flex-wrap gap-2.5 pb-8">
            {hits[0]?.date_published && (
              <CustomSortBy
                items={[
                  { value: ``, label: 'Trier par date' },
                  { value: `${INDEX_NEWS}:date_published:desc`, label: 'Plus récent' },
                  { value: `${INDEX_NEWS}:date_published:asc`, label: 'Plus ancien' },
                ]}
              />
            )}
            {hits[0]?.categories && (
              <CustomMenu attribute="categories" limit={AppConfig.meilisearchCustomMenuLimit} />
            )}
          </div>
        )}

      {results?.hitsPerPage !== HIT_PER_PAGE &&
        hits[0]?.type === 'directus_files' &&
        searchType === 'all' && (
          <div className="flex w-full flex-wrap gap-2.5 pb-8">
            {hits[0]?.ffbbsite_asset_type && (
              <CustomMenu
                attribute="ffbbsite_asset_type"
                limit={AppConfig.meilisearchCustomMenuLimit}
              />
            )}
            {hits[0]?.ffbbsite_asset_tag_parent && indexUiState?.menu?.ffbbsite_asset_type && (
              <CustomMenu
                attribute="ffbbsite_asset_tag_parent"
                limit={AppConfig.meilisearchCustomMenuLimit}
              />
            )}
            {hits[0]?.date_published && (
              <CustomSortBy
                items={[
                  { value: `${INDEX_ASSETS}:date_published:desc`, label: 'Plus récent' },
                  { value: `${INDEX_ASSETS}:date_published:asc`, label: 'Plus ancien' },
                ]}
              />
            )}
          </div>
        )}

      {searchType === 'all' && (
        <div
          className={
            hits[0]?.type === 'directus_files' || hits[0]?.type === 'ffbbsite_pages'
              ? 'mb-4 flex w-full flex-col gap-[3px]'
              : 'grid w-full grid-cols-1 gap-[3px] md:grid-cols-2 lg:grid-cols-4'
          }
        >
          {hits.map((hit, index) => {
            const cardInfo = hitMapper(hit);
            let image = cardInfo?.image;
            if (image && image.match(/width=/)) {
              image = image + '&height=340&fit=cover';
              cardInfo.image = image;
            }

            return (
              <div key={index}>
                {hit.type === 'directus_files' && (
                  <TableAssetItem
                    uploaded_on={cardInfo?.uploaded_on}
                    id={cardInfo?.id}
                    {...cardInfo}
                  />
                )}

                {hit.type === 'ffbbsite_pages' && <CardPage {...cardInfo} />}
                {hit.type !== 'ffbbsite_pages' && hit.type !== 'directus_files' && (
                  <div className="rounded-md border border-gray-100 bg-white p-5 lg:border-0 lg:bg-inherit lg:p-0">
                    <Card
                      titleBold={true}
                      type={'default'}
                      {...cardInfo}
                      imageHeight="h-[203px] lg:h-[120px] lg:w-[212px] xl:h-[178px] xl:w-[316px]"
                      imageSizes="(min-width: 66em) 17vw, 100vw"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {searchType === 'news' && (
        <div className="mb-4 grid w-full grid-cols-1 gap-x-4 md:grid-cols-2 lg:mb-4 lg:grid-cols-4">
          {modifiedHits.map((hit, index) => {
            const cardInfo = hitMapper(hit);
            let image = cardInfo.image;
            if (
              index === 0 &&
              hit.featured &&
              results?.page === 0 &&
              image &&
              image.match(/width=/)
            ) {
              image = image + '&height=600&fit=cover';
              cardInfo.image = image;
            } else if (image && image.match(/width=/)) {
              image = image + '&height=340&fit=cover';
              cardInfo.image = image;
            }
            return (
              <div
                key={index}
                className={
                  index === 0 && hit.featured && results?.page === 0
                    ? 'md:col-span-2 md:row-span-2'
                    : 'lg:mb-[70px]'
                }
              >
                <Card
                  titleBold={true}
                  type={index === 0 && hit.featured && results?.page === 0 ? 'image' : 'default'}
                  imageHeight={
                    index === 0 && hit.featured && results?.page === 0
                      ? 'h-[580px] xl:h-[680px] mb-4'
                      : 'h-[203px] lg:h-[120px] lg:w-[212px] xl:h-[178px] xl:w-[316px]'
                  }
                  {...cardInfo}
                  imageSizes={
                    hit.featured && results?.page === 0
                      ? '(min-width: 66em) 35vw, 100vw'
                      : '(min-width: 66em) 17vw, 100vw'
                  }
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
